import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IoMdEye, IoIosDocument, IoMdDownload } from 'react-icons/io';
import PageForm from '../../templates/PageForm';
import SelectFilter from '../../organisms/SelectFilter';
import { recommendationBFFService } from '../../../services/RecommendationBFFService';
import { AuthContext } from '../../../services/Auth/AuthContext';

interface BatchOption {
    sk: string;
    identification: string;
    id: string;
    documentsCount: string;
    provider: string;
    createdAt: string;  // Data de criação do lote
}

interface DocumentPLDAnalysis {
    document: string;
    entityName: string;
    entityType: 'CPF' | 'CNPJ';
    providers: {
        INOVAMIND: { recommendation: string; reasons: string[]; batchId: string } | null;
        ESCAVADOR: { recommendation: string; reasons: string[]; batchId: string } | null;
        QUOD: { recommendation: string; reasons: string[]; batchId: string } | null;
    };
    finalRecommendation: 'APPROVED' | 'REJECTED' | 'PENDING' | '';
    riskLevel?: 'BAIXO' | 'MEDIO' | 'ALTO' | 'EXTREMO' | '';
    creditFlag?: boolean;
    observations: string;
}

const RecommendationEngineParecerView: React.FC = () => {
    const navigate = useNavigate();
    const { batchSk } = useParams<{ batchSk: string }>();
    const { user } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sendingNotifications, setSendingNotifications] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Estados de progresso
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState('Carregando lotes...');
    
    // Estado do parecer salvo
    const [savedReport, setSavedReport] = useState<{id: string, batchSk: string, savedAt: string, analystId?: string} | null>(null);

    // Dados dos selects
    const [batchOptions, setBatchOptions] = useState<BatchOption[]>([]);
    const [selectedBatches, setSelectedBatches] = useState({
        INOVAMIND: '',
        ESCAVADOR: '',
        QUOD: ''
    });
    
    // Armazenar identifications dos lotes carregados
    const [batchIdentifications, setBatchIdentifications] = useState<{[key: string]: string}>({});

    // Dados dos documentos
    const [documents, setDocuments] = useState<DocumentPLDAnalysis[]>([]);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [selectedDocumentRules, setSelectedDocumentRules] = useState<string[]>([]);

    useEffect(() => {
        loadBatchOptions();
    }, []);

    const loadBatchOptions = async () => {
        try {
            // Usar BFF service em vez de chamada direta
            const data = await recommendationBFFService.fetchBatchList();
            
            // Filtrar apenas lotes dos providers PLD e status COMPLETED
            const pldProviders = ['INOVAMIND', 'ESCAVADOR', 'QUOD-PJ'];
            let filteredBatches = data.list.filter((batch: any) =>
                pldProviders.includes(batch.provider) && batch.status === 'COMPLETED'
            );

            // Filtrar últimos 15 dias
            const fifteenDaysAgo = new Date();
            fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
            
            filteredBatches = filteredBatches.filter((batch: any) => {
                if (!batch.createdAt) return true; // Manter lotes sem data (retrocompatibilidade)
                
                // Converter formato brasileiro "DD/MM/YYYY HH:MM" para Date
                const parseBrazilianDate = (dateStr: string): Date => {
                    const [datePart, timePart] = dateStr.split(' ');
                    const [day, month, year] = datePart.split('/');
                    const [hours, minutes] = (timePart || '00:00').split(':');
                    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
                };
                
                const batchDate = parseBrazilianDate(batch.createdAt);
                return batchDate >= fifteenDaysAgo;
            });

            // Ordenar por data (mais recente primeiro)
            filteredBatches.sort((a: any, b: any) => {
                const parseBrazilianDate = (dateStr: string): Date => {
                    if (!dateStr) return new Date(0);
                    const [datePart, timePart] = dateStr.split(' ');
                    const [day, month, year] = datePart.split('/');
                    const [hours, minutes] = (timePart || '00:00').split(':');
                    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
                };
                
                const dateA = parseBrazilianDate(a.createdAt || '');
                const dateB = parseBrazilianDate(b.createdAt || '');
                return dateB.getTime() - dateA.getTime();
            });

            setBatchOptions(filteredBatches);

        } catch (error) {
            console.error('Erro ao carregar lotes:', error);
            setError('Erro ao carregar lista de lotes');
        }
    };

    const handleBatchSelection = (provider: string, batchSk: string) => {
        setSelectedBatches(prev => ({
            ...prev,
            [provider]: batchSk
        }));
        
        // Não precisa carregar último parecer aqui
        // O checkExistingParecer já faz isso quando todos os lotes são selecionados
    };
    
    const clearFilters = () => {
        setSelectedBatches({
            INOVAMIND: '',
            ESCAVADOR: '',
            QUOD: ''
        });
        setDocuments([]);
        setSavedReport(null); // Limpar estado do parecer salvo
    };

    const loadDocuments = async () => {
        const selectedCount = Object.values(selectedBatches).filter(sk => sk).length;
        if (selectedCount === 0) {
            toast.warning('Selecione pelo menos um lote para carregar os documentos');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setLoadingProgress(0);
            setLoadingMessage(`Carregando ${selectedCount} lote(s)...`);

            const allDocuments = new Map<string, DocumentPLDAnalysis>();
            
            let completedBatches = 0;

            // ✅ OTIMIZAÇÃO: Carregar lotes em paralelo ao invés de sequencial
            const batchPromises = Object.entries(selectedBatches)
                .filter(([_, batchSk]) => batchSk)
                .map(async ([provider, batchSk]) => {
                    const normalizedProvider = provider === 'QUOD' ? 'QUOD-PJ' : provider;

                    try {
                        console.log(`🔄 Carregando lote ${provider} em paralelo...`);
                        setLoadingMessage(`Analisando lote ${provider}...`);
                        
                        const batchData = await recommendationBFFService.analyzeBatch(batchSk, (processed, total) => {
                            // Atualizar progresso por lote
                            const batchProgress = (processed / total) * (100 / selectedCount);
                            const totalProgress = (completedBatches * (100 / selectedCount)) + batchProgress;
                            setLoadingProgress(Math.round(totalProgress));
                            setLoadingMessage(`${provider}: ${processed}/${total} documentos`);
                        });
                        
                        completedBatches++;
                        await enrichWithPLDRecommendations(batchData, provider);
                        return { provider, batchData, success: true };
                    } catch (error) {
                        console.error(`❌ Erro ao carregar lote ${provider}:`, error);
                        toast.error(`Erro ao carregar dados do ${provider}`);
                        return { provider, batchData: null, success: false };
                    }
                });

            // Aguardar todos os lotes carregarem em paralelo
            setLoadingMessage('Aguardando conclusão de todos os lotes...');
            const batchResults = await Promise.all(batchPromises);

            // Processar resultados
            setLoadingMessage('Consolidando dados...');
            const identifications: {[key: string]: string} = {};
            
            for (const { provider, batchData, success } of batchResults) {
                if (!success || !batchData) continue;

                try {
                    // 🎯 Capturar identification do lote
                    if (batchData.identification) {
                        identifications[provider] = batchData.identification;
                        console.log(`📋 ${provider} identification: ${batchData.identification}`);
                    }
                    
                    // Processar documentos do lote
                    if (batchData.reports && Array.isArray(batchData.reports)) {
                        // Agrupar reports por documento para unificar CNPJs duplicados
                        const documentMap = new Map<string, {
                            report: any;
                            allReasons: Set<string>;
                            recommendation: string;
                        }>();

                        batchData.reports.forEach((report: any) => {
                            const document = report.document;
                            
                            // Normalizar recomendação PT → EN
                            let normalizedRec = 'PENDING';
                            if (report.backend_recommendation) {
                                const rec = report.backend_recommendation.toUpperCase();
                                if (rec === 'APROVADO') normalizedRec = 'APPROVED';
                                else if (rec === 'APPROVED') normalizedRec = 'APPROVED';
                                else if (rec === 'PENDENTE') normalizedRec = 'PENDING';
                                else if (rec === 'PENDING') normalizedRec = 'PENDING';
                                else if (rec === 'REPROVADO' || rec === 'REJEITADO') normalizedRec = 'REJECTED';
                                else if (rec === 'REJECTED') normalizedRec = 'REJECTED';
                            }
                            
                            if (!documentMap.has(document)) {
                                // Primeira ocorrência
                                documentMap.set(document, {
                                    report: report,
                                    allReasons: new Set(report.backend_reasons || []),
                                    recommendation: normalizedRec
                                });
                            } else {
                                // Documento duplicado - consolidar
                                const existing = documentMap.get(document)!;
                                
                                // Consolidar regras
                                if (report.backend_reasons && Array.isArray(report.backend_reasons)) {
                                    report.backend_reasons.forEach((rule: string) => existing.allReasons.add(rule));
                                }
                                
                                // ✅ Consolidar credit_flag (OR lógico - se qualquer report tiver, marca como true)
                                if (report.backend_credit_flag || report.backend_recommendation === 'SINALIZADO_CREDITO') {
                                    if (!existing.report.backend_credit_flag) {
                                        existing.report.backend_credit_flag = true;
                                    }
                                }
                                
                                // Prioridade: REJECTED > PENDING > APPROVED (pior caso)
                                if (normalizedRec === 'REJECTED') {
                                    existing.recommendation = 'REJECTED';
                                } else if (normalizedRec === 'PENDING' && existing.recommendation !== 'REJECTED') {
                                    existing.recommendation = 'PENDING';
                                }
                                // Se for APPROVED, mantém o existente (não piora)
                                
                                // Atualizar nome se o novo tiver e o existente não
                                const existingHasName = existing.report.name?.trim() || existing.report.nome?.trim();
                                const currentHasName = report.name?.trim() || report.nome?.trim();
                                if (currentHasName && !existingHasName) {
                                    existing.report = report;
                                }
                            }
                        });

                        // Adicionar/atualizar documentos no mapa geral
                        documentMap.forEach((consolidated) => {
                            const report = consolidated.report;
                            const document = report.document;
                            const entityType = report.backend_document_type ||
                                (document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ');

                            if (!allDocuments.has(document)) {
                                console.log('📝 Criando novo documento:', { 
                                    document, 
                                    provider,
                                    backend_credit_flag: report.backend_credit_flag,
                                    backend_recommendation: report.backend_recommendation
                                });
                                allDocuments.set(document, {
                                    document,
                                    entityName: '',
                                    entityType,
                                    providers: {
                                        INOVAMIND: null,
                                        ESCAVADOR: null,
                                        QUOD: null
                                    },
                                    finalRecommendation: '',
                                    creditFlag: report.backend_credit_flag || report.backend_recommendation === 'SINALIZADO_CREDITO' || false,
                                    observations: ''
                                });
                            }

                            const docAnalysis = allDocuments.get(document)!;
                            
                            // ✅ Atualizar creditFlag se vier do backend (OR lógico - se qualquer provider sinalizar)
                            if (report.backend_credit_flag || report.backend_recommendation === 'SINALIZADO_CREDITO') {
                                docAnalysis.creditFlag = true;
                            }

                            // Atualizar tipo se vier do backend
                            if (report.backend_document_type) {
                                docAnalysis.entityType = report.backend_document_type;
                            }

                            // Atualizar dados do provider
                            const providerKey = provider === 'QUOD-PJ' ? 'QUOD' : provider as keyof typeof docAnalysis.providers;

                            let recommendation = 'PENDING';
                            const reasons = Array.from(consolidated.allReasons);

                            if (consolidated.recommendation) {
                                switch (consolidated.recommendation.toUpperCase()) {
                                    case 'APPROVED':
                                    case 'APROVADO':
                                        recommendation = 'APPROVED';
                                        break;
                                    case 'REJECTED':
                                    case 'REJEITADO':
                                    case 'REPROVADO':
                                        recommendation = 'REJECTED';
                                        break;
                                    default:
                                        recommendation = 'PENDING';
                                }
                            }

                            docAnalysis.providers[providerKey] = {
                                recommendation,
                                reasons,
                                batchId: selectedBatches[provider as keyof typeof selectedBatches] || ''
                            };

                            // Atualizar nome com prioridade: INOVAMIND > ESCAVADOR > QUOD
                            const currentName = report.name?.trim() || report.nome?.trim() || '';
                            if (currentName) {
                                if (providerKey === 'INOVAMIND') {
                                    docAnalysis.entityName = currentName;
                                } else if (providerKey === 'ESCAVADOR' && !docAnalysis.entityName) {
                                    docAnalysis.entityName = currentName;
                                } else if (providerKey === 'QUOD' && !docAnalysis.entityName) {
                                    docAnalysis.entityName = currentName;
                                }
                            }
                        });
                    }

                } catch (error) {
                    console.warn(`Erro ao carregar lote ${batchSk} do ${provider}:`, error);
                }
            }

            setDocuments(Array.from(allDocuments.values()).map(doc => {
                // Calcular finalRecommendation baseado nas recomendações dos providers
                // Prioridade: REJECTED > PENDING > APPROVED (pior caso)
                let finalRecommendation: 'APPROVED' | 'REJECTED' | 'PENDING' | '' = '';
                let riskLevel: 'BAIXO' | 'MEDIO' | 'ALTO' | 'EXTREMO' | '' = '';
                
                const recommendations = [
                    doc.providers.INOVAMIND?.recommendation,
                    doc.providers.ESCAVADOR?.recommendation,
                    doc.providers.QUOD?.recommendation
                ].filter(Boolean);
                
                if (recommendations.length > 0) {
                    if (recommendations.some(r => r === 'REJECTED')) {
                        finalRecommendation = 'REJECTED';
                    } else if (recommendations.some(r => r === 'PENDING')) {
                        finalRecommendation = 'PENDING';
                    } else {
                        finalRecommendation = 'APPROVED';
                        // ✅ Se TODOS aprovados → Risco BAIXO
                        if (recommendations.every(r => r === 'APPROVED')) {
                            riskLevel = 'BAIXO';
                            console.log(`✅ Documento ${doc.document}: Todos aprovados → Risco BAIXO`);
                        }
                    }
                }
                
                return {
                    ...doc,
                    finalRecommendation,
                    riskLevel: riskLevel || doc.riskLevel // Manter riskLevel existente se não calculado
                };
            }));

            // ✅ REGRA DE APROVAÇÃO AUTOMÁTICA
            // Se TODOS os providers = APROVADO → PLD = APROVADO + Risco = BAIXO
            const allDocumentsArray = Array.from(allDocuments.values());
            const allApproved = allDocumentsArray.every(doc => {
                const recommendations = [
                    doc.providers.INOVAMIND?.recommendation,
                    doc.providers.ESCAVADOR?.recommendation,
                    doc.providers.QUOD?.recommendation
                ].filter(Boolean);
                
                return recommendations.length > 0 && recommendations.every(r => r === 'APPROVED');
            });
            
            if (allApproved && allDocumentsArray.length > 0) {
                console.log('✅ Todos os providers aprovados - Aplicando defaults automáticos');
                // Aplicar defaults via refs (assumindo que existem refs para os campos)
                // Nota: Isso será aplicado quando os campos forem renderizados
                // Por enquanto, vamos apenas logar. A aplicação real será no useEffect dos documentos
            }

            // Verificar se já existe parecer salvo para esta combinação de lotes
            await checkExistingParecer();
            
            // 🎯 Salvar identifications no estado
            setBatchIdentifications(identifications);
            console.log('📋 Identifications salvos:', identifications);

        } catch (error) {
            console.error('Erro ao carregar documentos:', error);
            setError('Erro ao carregar documentos dos lotes');
        } finally {
            setLoading(false);
        }
    };

    const checkExistingParecer = async () => {
        try {
            console.log('🔍 Verificando parecer existente...');
            console.log('📦 selectedBatches:', selectedBatches);
            
            // Criar identificador único baseado nos lotes selecionados
            const batchKey = Object.entries(selectedBatches)
                .filter(([_, sk]) => sk)
                .sort()
                .map(([provider, sk]) => `${provider}:${sk}`)
                .join('|');

            console.log('🔑 batchKey:', batchKey);

            if (!batchKey) {
                console.log('⚠️ batchKey vazio, pulando verificação');
                return;
            }

            // Verificar se existe parecer salvo no backend usando BFF Service
            console.log('📡 Chamando checkPLDReportExists...');
            const result = await recommendationBFFService.checkPLDReportExists(selectedBatches);
            console.log('📥 Resultado do check:', result);
            
            if (result.exists) {
                console.log('✅ Parecer encontrado!', result);
                setSavedReport({
                    id: result.report_id,
                    batchSk: result.batch_sk || '',
                    savedAt: new Date(result.saved_at).toLocaleDateString('pt-BR'),
                    analystId: result.analyst_id
                });
                
                // Carregar dados do parecer salvo nos documentos
                if (result.documents && result.documents.length > 0) {
                    console.log('📋 Carregando dados do parecer salvo:', result.documents.length, 'documentos');
                    console.log('📋 Documentos salvos:', result.documents);
                    
                    // Atualizar documentos com dados salvos
                    setDocuments(prevDocs => {
                        return prevDocs.map(doc => {
                            const savedDoc = result.documents.find((d: any) => d.document === doc.document);
                            if (savedDoc) {
                                console.log(`✅ Restaurando dados para ${doc.document}:`, savedDoc);
                                return {
                                    ...doc,
                                    finalRecommendation: savedDoc.finalRecommendation || doc.finalRecommendation,
                                    riskLevel: savedDoc.riskLevel || doc.riskLevel,
                                    observations: savedDoc.observations || doc.observations
                                };
                            }
                            return doc;
                        });
                    });
                }
            } else {
                console.log('ℹ️ Nenhum parecer salvo encontrado');
            }
        } catch (error) {
            console.error('❌ Erro ao verificar parecer existente:', error);
            // Não mostrar erro para o usuário, é apenas uma verificação
        }
    };

    const enrichWithPLDRecommendations = async (batchData: any, provider: string) => {
        // ✅ NÃO FAZ NADA - O BFF JÁ RETORNA OS DADOS ENRIQUECIDOS
        // O método analyzeBatch do BFF já:
        // 1. Agrupa reports por documento (ESCAVADOR)
        // 2. Chama recommendation-engine com dados agrupados
        // 3. Enriquece todos os reports com backend_recommendation, backend_reasons, etc.
        
        console.log(`✅ Parecer: Dados já enriquecidos pelo BFF - ${batchData.reports?.length || 0} reports`);
        
        // Apenas validar que os dados vieram corretos
        if (batchData.reports && Array.isArray(batchData.reports)) {
            const enrichedCount = batchData.reports.filter((r: any) => r.backend_recommendation).length;
            console.log(`📊 Parecer: ${enrichedCount}/${batchData.reports.length} reports com recomendação`);
        }
    };

    // Função de fallback com regras hardcoded (mantida para compatibilidade)
    const applyPLDRulesDirectlyFallback = (report: any, provider: string): string[] => {
        const reasons: string[] = [];

        if (provider === 'INOVAMIND') {
            // Aplicar regras do INOVAMIND baseadas nos dados reais
            if (report.bacen_qgi_cpf === true) {
                reasons.push('Quadro Geral de Inabilitados do Banco Central');
            }
            if (report.work_slave_cnpj === true || report.work_slave_cpf === true) {
                reasons.push('Trabalho Escravo');
            }
            if (report.criminal_record_federal_police === true) {
                reasons.push('Antecedentes Criminais (PF)');
            }
            if (report.sanctions_list === true) {
                reasons.push('Sanções OFAC');
            }
            if (report.eu_sanction_list === true) {
                reasons.push('Sanções UE');
            }
            if (report.fr_sanction_list === true) {
                reasons.push('Sanções França');
            }
            if (report.uk_sanction_list === true) {
                reasons.push('Sanções UK');
            }
            if (report.onu_sanction_lists === true) {
                reasons.push('Sanções ONU');
            }
            if (report.us_nonproliferation_sanction_list === true) {
                reasons.push('Sanções EUA');
            }
            if (report.world_bank_ineligible_sanction_list === true) {
                reasons.push('Banco Mundial');
            }
            if (report.red_notice_interpol === true) {
                reasons.push('Red Notice Interpol');
            }
            if (report.pep_search_v2 === true) {
                reasons.push('Pessoa Exposta Politicamente (PEP)');
            }
            if (report.ceis_infos_government_base_cnpj === true || report.ceis_infos_government_base_cpf === true) {
                reasons.push('CEIS');
            }
            if (report.cnep_infos_government_base_cnpj === true) {
                reasons.push('CNEP');
            }
            if (report.ceaf_infos_government_base === true) {
                reasons.push('CEAF');
            }
            if (report.ibama_assessment_cnpj === true || report.ibama_assessment_cpf === true) {
                reasons.push('IBAMA Multas');
            }
            if (report.ibama_embargo_cnpj === true || report.ibama_embargo_cpf === true) {
                reasons.push('IBAMA Embargos');
            }
        }

        else if (provider === 'ESCAVADOR') {
            // Aplicar regras do ESCAVADOR baseadas nos processos
            if (report.processos && Array.isArray(report.processos)) {
                const assuntos = report.processos.map((p: any) => p.sigla_assunto || '');

                if (assuntos.includes('CLP')) {
                    reasons.push('Crimes contra a liberdade pessoal');
                }
                if (assuntos.includes('RCAE')) {
                    reasons.push('Redução a condição análoga à de escravo');
                }
                if (assuntos.includes('RJF')) {
                    reasons.push('Recuperação Judicial/Falência');
                }
                if (assuntos.includes('DUP')) {
                    reasons.push('Duplicata');
                }
                if (assuntos.includes('COT')) {
                    reasons.push('Crimes contra a Ordem Tributária');
                }
            }
        }

        else if (provider === 'QUOD' || provider === 'QUOD-PJ') {
            // Aplicar regras do QUOD
            if (report.CompanyStatus && report.CompanyStatus !== 'ATIVO') {
                reasons.push(`Status RF: ${report.CompanyStatus}`);
            }

            // Verificar CNAE na blocklist
            const blockedCnaes = ['6201500']; // Exemplo
            if (report.MainActivity && blockedCnaes.includes(report.MainActivity)) {
                reasons.push(`CNAE bloqueado: ${report.MainActivity}`);
            }

            // Verificar pendências de crédito
            if (report.pendenciesControlCred && parseFloat(report.pendenciesControlCred) > 5000) {
                reasons.push(`Pendências de crédito: R$ ${parseFloat(report.pendenciesControlCred).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
            }

            // Verificar sócios com restrição
            if (report.partnerRestriction === true) {
                reasons.push('Sócio com restrição');
            }
        }

        // Se não há regras acionadas, não adicionar "Nenhuma restrição PLD encontrada"
        // Isso será tratado na exibição do modal

        return reasons;
    };

    const updateFinalRecommendation = (index: number, recommendation: 'APPROVED' | 'REJECTED' | 'PENDING' | '') => {
        const updatedDocuments = [...documents];
        updatedDocuments[index].finalRecommendation = recommendation;
        setDocuments(updatedDocuments);
    };

    const updateRiskLevel = (index: number, riskLevel: 'BAIXO' | 'MEDIO' | 'ALTO' | 'EXTREMO' | '') => {
        const updatedDocuments = [...documents];
        updatedDocuments[index].riskLevel = riskLevel;
        setDocuments(updatedDocuments);
    };
    
    // ✅ REGRA DE APROVAÇÃO AUTOMÁTICA
    // Quando documentos são carregados, verificar se todos providers = APROVADO
    const defaultsAppliedRef = useRef(false);
    
    useEffect(() => {
        if (documents.length === 0) return;
        if (defaultsAppliedRef.current) return; // ✅ Evita loop - só aplica uma vez
        
        const allApproved = documents.every(doc => {
            const recommendations = [
                doc.providers.INOVAMIND?.recommendation,
                doc.providers.ESCAVADOR?.recommendation,
                doc.providers.QUOD?.recommendation
            ].filter(Boolean);
            
            return recommendations.length > 0 && recommendations.every(r => r === 'APPROVED');
        });
        
        if (allApproved) {
            console.log('✅ Todos os providers aprovados - Aplicando defaults: PLD=APROVADO, Risco=BAIXO');
            
            // Aplicar defaults para todos os documentos
            const updatedDocuments = documents.map(doc => ({
                ...doc,
                finalRecommendation: 'APPROVED' as const,
                riskLevel: 'BAIXO' as const
            }));
            
            setDocuments(updatedDocuments);
            defaultsAppliedRef.current = true; // ✅ Marca como aplicado
        }
    }, [documents]); // Executar quando documentos mudarem

    const updateObservations = (index: number, observations: string) => {
        const updatedDocuments = [...documents];
        updatedDocuments[index].observations = observations;
        setDocuments(updatedDocuments);
    };

    const getRiskLevelBadge = (riskLevel?: string) => {
        if (!riskLevel) return null;

        const styles = {
            'BAIXO': 'bg-green-100 text-green-800 border-green-300',
            'MEDIO': 'bg-yellow-100 text-yellow-800 border-yellow-300',
            'ALTO': 'bg-red-100 text-red-800 border-red-300',
            'EXTREMO': 'bg-gray-900 text-white border-gray-900'
        };

        const labels = {
            'BAIXO': 'Baixo',
            'MEDIO': 'Médio',
            'ALTO': 'Alto',
            'EXTREMO': 'Extremo'
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[riskLevel as keyof typeof styles]}`}>
                {labels[riskLevel as keyof typeof labels]}
            </span>
        );
    };

    const showRules = (doc: DocumentPLDAnalysis) => {
        const allRules: string[] = [];

        Object.entries(doc.providers).forEach(([provider, data]) => {
            if (data && data.reasons.length > 0) {
                // Filtrar "Nenhuma restrição PLD encontrada"
                const validReasons = data.reasons.filter(reason =>
                    reason !== 'Nenhuma restrição PLD encontrada'
                );

                if (validReasons.length > 0) {
                    validReasons.forEach(reason => {
                        allRules.push(`${provider}: ${reason}`);
                    });
                } else {
                    // Só mostrar "Nenhuma restrição" se não há regras válidas
                    allRules.push(`${provider}: Nenhuma restrição PLD encontrada`);
                }
            } else {
                // Provider sem dados
                allRules.push(`${provider}: Nenhuma restrição PLD encontrada`);
            }
        });

        setSelectedDocumentRules(allRules);
        setShowRulesModal(true);
    };

    const getRecommendationBadge = (recommendation: string | null) => {
        if (!recommendation || recommendation === 'PENDING') {
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pendente</span>;
        }

        const badges: Record<string, { className: string; label: string }> = {
            'APPROVED': { className: 'px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Aprovado' },
            'REJECTED': { className: 'px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Reprovado' }
        };

        const badge = badges[recommendation] || badges['APPROVED'];
        return <span className={badge.className}>{badge.label}</span>;
    };

    const translateRecommendation = (recommendation: string): string => {
        const translations: Record<string, string> = {
            'APPROVED': 'Aprovação',
            'REJECTED': 'Reprovação',
            'PENDING': 'Pendente'
        };
        return translations[recommendation] || recommendation;
    };

    const hasCreditFlag = (doc: DocumentPLDAnalysis): boolean => {
        return doc.creditFlag === true;
    };

    const translateRiskLevel = (riskLevel: string): string => {
        const translations: Record<string, string> = {
            'BAIXO': 'Baixo',
            'MEDIO': 'Médio',
            'ALTO': 'Alto'
        };
        return translations[riskLevel] || riskLevel;
    };

    const exportToCSV = () => {
        if (documents.length === 0) {
            toast.warning('Nenhum documento para exportar');
            return;
        }

        const headers = [
            'Documento',
            'Nome',
            'Tipo',
            'INOVAMIND',
            'ESCAVADOR',
            'QUOD',
            'Recomendação PLD',
            'Sinalizado Crédito',
            'Nível de Risco',
            'Observações',
            'Regras Acionadas'
        ];

        const csvData = documents.map(doc => [
            doc.document,
            doc.entityName,
            doc.entityType === 'CPF' ? 'PF' : 'PJ',
            doc.providers.INOVAMIND ? translateRecommendation(doc.providers.INOVAMIND.recommendation) : '-',
            doc.providers.ESCAVADOR ? translateRecommendation(doc.providers.ESCAVADOR.recommendation) : '-',
            doc.providers.QUOD ? translateRecommendation(doc.providers.QUOD.recommendation) : '-',
            doc.finalRecommendation ? translateRecommendation(doc.finalRecommendation) : 'Pendente',
            hasCreditFlag(doc) ? 'Sim' : 'Não',
            doc.riskLevel ? translateRiskLevel(doc.riskLevel) : 'Não informado',
            doc.observations || '',
            getAllRulesText(doc)
        ]);

        const csvContent = [headers, ...csvData]
            .map(row => row.map(field => `"${field}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `parecer-pld-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getAllRulesText = (doc: DocumentPLDAnalysis): string => {
        const allRules: string[] = [];

        Object.entries(doc.providers).forEach(([provider, data]) => {
            if (data && data.reasons.length > 0) {
                data.reasons.forEach(reason => {
                    allRules.push(`${provider}: ${reason}`);
                });
            }
        });

        return allRules.join('; ');
    };

    const saveParecer = async () => {
        // Validar se todos os documentos têm pelo menos uma recomendação de provider
        const missingProviderRecommendations = documents.filter(doc =>
            !doc.providers.INOVAMIND && !doc.providers.ESCAVADOR && !doc.providers.QUOD
        );
        if (missingProviderRecommendations.length > 0) {
            toast.error('Todos os documentos devem ter pelo menos uma recomendação de provider');
            return;
        }

        try {
            setSaving(true);

            const parecerData = {
                batchSelections: selectedBatches,
                batchIdentification: Object.values(batchIdentifications).filter(Boolean).join(' | '),
                documents: documents,
                createdAt: new Date().toISOString()
            };
            
            console.log('📋 Enviando parecer com identification:', parecerData.batchIdentification);

            // Usar BFF Service para salvar parecer
            const result = await recommendationBFFService.savePLDReport(parecerData);
            console.log('Parecer salvo:', result);

            // Salvar informações do parecer salvo
            const savedAt = new Date().toLocaleDateString('pt-BR');
            setSavedReport({
                id: result.report_id,
                batchSk: result.batch_sk,
                savedAt: savedAt,
                analystId: user?.username || user?.email || 'system'
            });

            // Não mostrar toast, apenas atualizar estado para exibir mensagem inline
        } catch (error) {
            console.error('Erro ao salvar parecer:', error);
            toast.error('Erro ao salvar parecer PLD');
        } finally {
            setSaving(false);
        }
    };

    const hasNotifiableConditions = (): boolean => {
        return documents.some(doc =>
            doc.creditFlag === true || (doc.riskLevel && ['ALTO', 'EXTREMO'].includes(doc.riskLevel))
        );
    };

    const sendNotifications = async () => {
        if (!savedReport) return;

        try {
            setSendingNotifications(true);
            const parecerData = {
                batchSelections: selectedBatches,
                batchIdentification: Object.values(batchIdentifications).filter(Boolean).join(' | '),
                documents: documents,
            };
            const result = await recommendationBFFService.sendPLDNotifications(savedReport.batchSk, parecerData);
            if (result.events_published > 0) {
                toast.success(`${result.events_published} notificação(ões) enviada(s) com sucesso`);
            } else {
                toast.info('Nenhuma notificação a enviar (condições não atendidas)');
            }
        } catch (error) {
            console.error('Erro ao enviar notificações:', error);
            toast.error('Erro ao enviar notificações');
        } finally {
            setSendingNotifications(false);
        }
    };

    const getProviderOptions = (provider: string) => {
        const normalizedProvider = provider === 'QUOD' ? 'QUOD-PJ' : provider;
        return batchOptions.filter(batch => batch.provider === normalizedProvider);
    };

    return (
        <PageForm
            pageTitle="Parecer PLD - Análise Multi-Provider"
            apiVersion="Recommendation Engine - 1.0.0"
            isLoading={false}
            breadcrumb={[
                { label: 'Pareceres PLD', url: '/recommendation-engine/pareceres' },
                { label: 'Novo Parecer', url: '#' }
            ]}
            controlSlot={<div></div>}
        >
            {/* Loading Overlay com Progresso */}
            {loading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-8 max-w-md w-full mx-4">
                        <div className="flex flex-col space-y-6">
                            {/* Barra de progresso REAL */}
                            <div className="w-full">
                                <div className="flex mb-2 items-center justify-between">
                                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                                        {loadingProgress}% concluído
                                    </span>
                                </div>
                                <div className="overflow-hidden h-3 mb-4 text-xs flex rounded-full bg-blue-100">
                                    <div 
                                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-300"
                                        style={{ width: `${loadingProgress}%` }}
                                    ></div>
                                </div>
                            </div>
                            
                            {/* Mensagem dinâmica */}
                            <div className="flex items-center space-x-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <span className="text-lg text-gray-700 dark:text-gray-200 font-medium">{loadingMessage}</span>
                            </div>
                            
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                                Aguarde enquanto processamos os lotes
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Seleção de Lotes */}
            <div className="mb-6 bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Seleção de Lotes por Provider
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {['INOVAMIND', 'ESCAVADOR', 'QUOD'].map(provider => (
                        <SelectFilter
                            key={provider}
                            id={`select-${provider}`}
                            label={provider}
                            value={selectedBatches[provider as keyof typeof selectedBatches]}
                            onChange={(value) => handleBatchSelection(provider, value)}
                            defaultVoidOption={true}
                            defaultLabel="Selecione um lote..."
                            options={getProviderOptions(provider).map(batch => ({
                                value: batch.sk,
                                label: `${batch.identification} - ${batch.id} (${batch.documentsCount} docs)`
                            }))}
                            className="w-full"
                        />
                    ))}
                </div>

                <div className="flex justify-between items-center">
                    <div className="flex gap-2 items-center">
                        <button
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            onClick={loadDocuments}
                            disabled={loading}
                        >
                            {loading ? 'Carregando...' : 'Carregar Documentos'}
                        </button>

                        <button
                            className="text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50"
                            onClick={exportToCSV}
                            disabled={documents.length === 0 || !savedReport}
                            title={!savedReport ? "Salve o parecer antes de exportar" : ""}
                        >
                            <IoMdDownload />
                            Exportar CSV
                        </button>

                        <button
                            className="text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={saveParecer}
                            disabled={saving || documents.length === 0}
                        >
                            <IoIosDocument />
                            {saving ? 'Salvando...' : (savedReport ? 'Salvar Nova Versão' : 'Salvar Parecer')}
                        </button>

                        <button
                            className="text-white bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={sendNotifications}
                            disabled={sendingNotifications || !savedReport || !hasNotifiableConditions()}
                            title={!savedReport ? 'Salve o parecer primeiro' : !hasNotifiableConditions() ? 'Nenhum documento com risco alto/extremo ou crédito sinalizado' : 'Enviar notificações para o Slack'}
                        >
                            {sendingNotifications ? 'Enviando...' : 'Enviar Notificações'}
                        </button>

                        {/* Feedback de parecer salvo - inline e verde */}
                        {savedReport && !saving && (
                            <div className="px-4 py-2 bg-green-100 border border-green-400 text-green-700 rounded-md flex items-center gap-2">
                                <span className="font-medium">✓ Parecer carregado</span>
                                <span className="text-sm">Salvo em: {savedReport.savedAt}</span>
                            </div>
                        )}
                    </div>

                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                        onClick={clearFilters}
                    >
                        Limpar Filtros
                    </button>
                </div>
            </div>

            {/* Alerta de Último Parecer Salvo */}

            {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                </div>
            )}

            {/* Alerta: Parecer Já Existe */}
            {savedReport && documents.length > 0 && (
                <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                            <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                                ⚠️ Parecer Já Existe
                            </h4>
                            <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
                                Este lote já possui parecer salvo em: <strong>{savedReport.savedAt}</strong>
                                {savedReport.analystId && (
                                    <> por <strong>{savedReport.analystId}</strong></>
                                )}
                            </p>
                            <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                💡 Você pode editar os campos abaixo. Ao salvar, será criada uma <strong>NOVA versão</strong> do parecer.
                                <br />
                                O parecer anterior será mantido no histórico.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Resumo */}
            {documents.length > 0 && (
                <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Total Documentos</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {documents.length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Aprovados</div>
                        <div className="text-lg font-semibold text-green-600">
                            {documents.filter(d => d.finalRecommendation === 'APPROVED').length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Reprovados</div>
                        <div className="text-lg font-semibold text-red-600">
                            {documents.filter(d => d.finalRecommendation === 'REJECTED').length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Pendentes</div>
                        <div className="text-lg font-semibold text-yellow-600">
                            {documents.filter(d => !d.finalRecommendation || d.finalRecommendation === 'PENDING').length}
                        </div>
                    </div>
                </div>
            )}

            {/* Tabela de Documentos */}
            {documents.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-slate-700">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Documento
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Nome
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Tipo
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        INOVAMIND
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        ESCAVADOR
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        QUOD
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Recomendação PLD
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Nível de Risco
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Crédito Sinalizado
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Observações
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Regras
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {documents.map((doc, index) => {
                                    return (
                                    <tr key={`${doc.document}-${index}`} className="hover:bg-gray-100 dark:hover:bg-slate-600">
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {doc.document}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-xs">
                                            <div className="truncate" title={doc.entityName}>
                                                {doc.entityName || '-'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                {doc.entityType === 'CPF' ? 'PF' : 'PJ'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {doc.providers.INOVAMIND ? getRecommendationBadge(doc.providers.INOVAMIND.recommendation) :
                                                <span className="text-gray-500">-</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            {doc.providers.ESCAVADOR ? getRecommendationBadge(doc.providers.ESCAVADOR.recommendation) :
                                                <span className="text-gray-500">-</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            {doc.providers.QUOD ? getRecommendationBadge(doc.providers.QUOD.recommendation) :
                                                <span className="text-gray-500">-</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <SelectFilter
                                                id={`final-recommendation-${index}`}
                                                label=""
                                                value={doc.finalRecommendation}
                                                onChange={(value) => updateFinalRecommendation(index, value as any)}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione..."
                                                options={[
                                                    { value: 'APPROVED', label: 'Aprovação' },
                                                    { value: 'REJECTED', label: 'Reprovação' },
                                                    { value: 'PENDING', label: 'Pendente' }
                                                ]}
                                                className={`w-full ${
                                                    savedReport ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                                                } ${
                                                    doc.finalRecommendation === 'APPROVED' ? 'border-2 border-green-500' :
                                                    doc.finalRecommendation === 'REJECTED' ? 'border-2 border-red-500' :
                                                    doc.finalRecommendation === 'PENDING' ? 'border-2 border-yellow-500' :
                                                    ''
                                                }`}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <SelectFilter
                                                id={`risk-level-${index}`}
                                                label=""
                                                value={doc.riskLevel || ''}
                                                onChange={(value) => updateRiskLevel(index, value as any)}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione..."
                                                options={[
                                                    { value: 'BAIXO', label: 'Baixo' },
                                                    { value: 'MEDIO', label: 'Médio' },
                                                    { value: 'ALTO', label: 'Alto' },
                                                    { value: 'EXTREMO', label: 'Extremo' }
                                                ]}
                                                className={`w-full ${
                                                    savedReport ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                                                } ${
                                                    doc.riskLevel === 'BAIXO' ? 'border-2 border-green-500' :
                                                    doc.riskLevel === 'MEDIO' ? 'border-2 border-yellow-500' :
                                                    doc.riskLevel === 'ALTO' ? 'border-2 border-red-500' :
                                                    doc.riskLevel === 'EXTREMO' ? 'border-2 border-gray-900' :
                                                    ''
                                                }`}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-center">
                                                <input
                                                    type="checkbox"
                                                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded accent-blue-600 pointer-events-none"
                                                    checked={doc.creditFlag ?? false}
                                                    readOnly
                                                    title="Valor vem das regras aplicadas (não editável)"
                                                />
                                            </div>
                                        </td>

                                        <td className="px-4 py-3">
                                            <textarea
                                                className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-gray-600 dark:text-white ${
                                                    savedReport ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                                                }`}
                                                placeholder="Observações..."
                                                value={doc.observations}
                                                onChange={(e) => updateObservations(index, e.target.value)}
                                                rows={3}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 text-sm flex items-center justify-center p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900"
                                                onClick={() => showRules(doc)}
                                                title="Ver Regras"
                                            >
                                                <IoMdEye className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal de Regras */}
            {showRulesModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Regras Acionadas
                        </h3>

                        {selectedDocumentRules.length > 0 ? (
                            <ul className="space-y-2">
                                {selectedDocumentRules.map((rule, index) => (
                                    <li key={index} className="text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-slate-700 rounded">
                                        {rule}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400">Nenhuma regra acionada</p>
                        )}

                        <div className="mt-6 flex justify-end">
                            <button
                                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                                onClick={() => setShowRulesModal(false)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageForm>
    );
};

export default RecommendationEngineParecerView;
